import { navigate } from 'gatsby';
import React, { useCallback, useState } from 'react';

import { useEditContext } from './EditContext';
import * as styles from './EditToolbar.css';
import { commitAll, getFileContent, deleteFile as ghDeleteFile } from './github';
import { generateSlug, serializeContentFile } from './serialization';

function CreateModal({
  type,
  onClose,
}: {
  type: 'koolitus' | 'artikkel';
  onClose: () => void;
}) {
  const { token, toast: _toast } = useEditContext();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(
    new Date().toLocaleDateString('et-EE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  );
  const [author, setAuthor] = useState('');
  const [creating, setCreating] = useState(false);

  const collection = type === 'koolitus' ? 'koolitused' : 'lugemist';

  const handleCreate = useCallback(async () => {
    if (!title || !token) return;
    setCreating(true);

    try {
      const slug = generateSlug(title);
      const fm: Record<string, string> = { title, date };
      if (type === 'artikkel' && author) fm.author = author;

      const content = serializeContentFile(fm, '');
      const filePath = `_content/${collection}/${slug}.md`;

      await commitAll(
        [{ path: filePath, content, encoding: 'utf-8' }],
        `Create ${type}: ${title}`,
        token
      );

      onClose();
      navigate(`/${collection}/${slug}`);
    } catch (e) {
      alert(`Failed to create: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }, [title, date, author, type, collection, token, onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '32px',
          minWidth: '360px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          fontFamily: "'Neuton', serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: '22px', color: '#2B7A67' }}>
          {type === 'koolitus' ? 'Uus koolitus' : 'Uus artikkel'}
        </h2>

        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#5a5a5a' }}>
            Pealkiri
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: "'Neuton', serif",
              boxSizing: 'border-box',
            }}
            autoFocus
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#5a5a5a' }}>
            Kuupäev
          </span>
          <input
            type="text"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="DD.MM.YYYY"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: "'Neuton', serif",
              boxSizing: 'border-box',
            }}
          />
        </label>

        {type === 'artikkel' && (
          <label style={{ display: 'block', marginBottom: '12px' }}>
            <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#5a5a5a' }}>
              Autor
            </span>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: "'Neuton', serif",
                boxSizing: 'border-box',
              }}
            />
          </label>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button className={styles.cancelButton} onClick={onClose}>
            Tühista
          </button>
          <button
            className={styles.saveButton}
            onClick={handleCreate}
            disabled={!title || creating}
          >
            {creating ? 'Loob...' : 'Loo'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditToolbar() {
  const {
    isAuthenticated,
    isEditMode,
    isSaving,
    pendingChanges,
    token,
    login,
    logout,
    toggleEditMode,
    saveAll,
    discardAll,
    toast,
  } = useEditContext();

  const [createType, setCreateType] = useState<'koolitus' | 'artikkel' | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [mounted, setMounted] = useState(false);

  React.useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  const isContentPage =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/koolitused/') ||
      window.location.pathname.startsWith('/lugemist/'));

  const isDetailPage =
    isContentPage &&
    window.location.pathname.split('/').filter(Boolean).length >= 2;

  const handleDelete = async () => {
    if (!token || !isDetailPage) return;

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const collection = pathParts[0];
    const slug = decodeURIComponent(pathParts[1]);
    const filePath = `_content/${collection}/${slug}.md`;

    if (!window.confirm('Kustuta see sisu? Seda ei saa tagasi võtta.')) return;

    try {
      const { sha } = await getFileContent(filePath, token);
      await ghDeleteFile(filePath, sha, `Delete ${filePath}`, token);
      navigate(`/${collection}`);
    } catch (e) {
      alert(`Delete failed: ${(e as Error).message}`);
    }
  };

  return (
    <>
      {toast && <div className={styles.toast}>{toast}</div>}
      {createType && (
        <CreateModal type={createType} onClose={() => setCreateType(null)} />
      )}

      {isAuthenticated && <div className={styles.toolbar}>
        {!isEditMode && (
          <>
            <button className={styles.editButton} onClick={toggleEditMode}>
              &#9998; Muuda
            </button>
            <button className={styles.logoutButton} onClick={logout}>
              Logi välja
            </button>
          </>
        )}

        {isAuthenticated && isEditMode && (
          <>
            {pendingChanges.size > 0 && (
              <>
                <button
                  className={styles.saveButton}
                  onClick={saveAll}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className={styles.spinner} /> Salvestab...
                    </>
                  ) : (
                    <>
                      Salvesta
                      <span className={styles.changeCount}>
                        {pendingChanges.size}
                      </span>
                    </>
                  )}
                </button>
                <button
                  className={styles.discardButton}
                  onClick={discardAll}
                  disabled={isSaving}
                >
                  Tühista
                </button>
              </>
            )}

            <div style={{ position: 'relative' }}>
              <button
                className={styles.editButton}
                onClick={() => setShowMenu(!showMenu)}
              >
                +
              </button>
              {showMenu && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '8px',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    padding: '4px',
                    minWidth: '160px',
                    fontFamily: "'Neuton', serif",
                  }}
                >
                  <button
                    className={styles.menuItemButton}
                    onClick={() => {
                      setCreateType('koolitus');
                      setShowMenu(false);
                    }}
                  >
                    Uus koolitus
                  </button>
                  <button
                    className={styles.menuItemButton}
                    onClick={() => {
                      setCreateType('artikkel');
                      setShowMenu(false);
                    }}
                  >
                    Uus artikkel
                  </button>
                  {isDetailPage && (
                    <>
                      <div style={{ height: '1px', background: '#eee', margin: '4px 0' }} />
                      <button
                        className={styles.menuItemButton}
                        style={{ color: '#c44' }}
                        onClick={() => {
                          handleDelete();
                          setShowMenu(false);
                        }}
                      >
                        Kustuta
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <button className={styles.cancelButton} onClick={toggleEditMode}>
              {pendingChanges.size === 0 ? 'Lõpeta muutmine' : 'Loobu'}
            </button>
          </>
        )}
      </div>}
    </>
  );
}
